import { HttpErrorResponse } from '@angular/common/http';
import {
  Activity,
  Amenity,
  IBoondock,
  IBoondockWhere,
  IBoondockPoint,
  IPhoto,
  ISubPhoto,
  Policy,
  SignalReception,
} from '@curbnturf/entities';
import { createAction, props } from '@ngrx/store';

export enum BoondockActionTypes {
  CreateBoondock = '[Boondock] Create Boondock',
  CreateActivity = '[Boondock] Create Activity',
  CreateAmenity = '[Boondock] Create Amenity',
  CreatePolicy = '[Boondock] Create Policy',
  CreateSignalReception = '[Boondock] Create Signal Reception',
  CreateBoondockFailed = '[Boondock] Create Boondock Failed',
  LoadBoondock = '[Boondock] Load Data',
  LoadBoondocks = '[Boondock] Load Boondocks',
  LoadBoondockFailed = '[Boondock] Load Data Failed',
  LoadBoondockRatings = '[Boondock] Load Boondock Ratings',
  ClearBoondocks = '[Boondock] Clear Boondocks',
  UpdateBoondock = '[Boondock] Update',
  UpdateBoondockFailed = '[Boondock] Update Failed',
  UploadBoondockImage = '[Boondock] Upload Boondock Image',
  UploadBoondockReviewImage = '[Boondock] Upload Boondock Review Image',
  BoondockReviewImageUploaded = '[Boondock] Uploaded Boondock Review Image',
  BoondockReviewImageUploadFailed = '[Boondock] Upload Failed Boondock Review Image',
  RemoveImageFromBoondock = '[Boondock] Remove Image',
  RemoveBoondock = '[Boondock] Remove Boondock',
  RemoveBoondockFailed = '[Boondock] Remove Boondock Failed',
  RestoreBoondock = '[Boondock] Restore Boondock',
  RemoveActivity = '[Boondock] Remove Activity',
  RemoveAmenity = '[Boondock] Remove Amenity',
  RemovePolicy = '[Boondock] Remove Policy',
  RemoveSignalReception = '[Boondock] Remove Signal Reception',
  BoondockCreated = '[Boondock] Created',
  BoondockImageRemoved = '[Boondock] Image Removed',
  BoondockLoaded = '[Boondock] Data Loaded',
  BoondocksLoaded = '[Boondock] Data For Boondocks Loaded',
  BoondockRemoved = '[Boondock] Removed',
  BoondockRestored = '[Boondock] Restored',
  BoondockUpdated = '[Boondock] Updated',
  BoondockImagesUpdated = '[Boondock] Images Updated',
  EnableBoondock = '[Boondock] Enable Boondock',
  DisableBoondock = '[Boondock] Disable Boondock',
  BoondockDisabled = '[Boondock] Boondock Disabled',
  BoondockDisabledFailed = '[Boondock] Boondock Disable Failed',
  BoondockEnabled = '[Boondock] Boondock Enabled',
  BoondockEnabledFailed = '[Boondock] Boondock Enable Failed',
  SelectBoondock = '[Boondock] Select Boondock',
  SelectBoondockByBoondock = '[Boondock] Select By Boondock',
  BoondockSelected = '[Boondock] Boondock Selected',
  FavoriteListing = '[Boondock] Favorite Boondock',
  UnfavoriteListing = '[Boondock] Unfavorite Boondock',
  ListingFavorCompleted = '[Boondock] Boondock Favorite/Unfavorite Completed',
  ListingFavorFailed = '[Boondock] Boondock Favorite/Unfavorite Failed',
  BoondockRecropImage = '[Boondock] Boondock Recrop Image',
  BoondockRecropImageFailure = '[Boondock] Boondock Recrop Image Failure',
}

export const createBoondock = createAction(
  BoondockActionTypes.CreateBoondock,
  props<{ boondock: IBoondock }>(),
);

export const createActivity = createAction(
  BoondockActionTypes.CreateActivity,
  props<{ boondock: IBoondock }>(),
);

export const createAmenity = createAction(
  BoondockActionTypes.CreateAmenity,
  props<{ boondock: IBoondock }>(),
);

export const createPolicy = createAction(
  BoondockActionTypes.CreatePolicy,
  props<{ boondock: IBoondock }>(),
);

export const createSignalReception = createAction(
  BoondockActionTypes.CreateSignalReception,
  props<{ boondock: IBoondock }>(),
);

export const createBoondockFailed = createAction(
  BoondockActionTypes.CreateBoondockFailed,
  props<{ error: string }>(),
);

export const loadBoondock = createAction(
  BoondockActionTypes.LoadBoondock,
  props<{ boondockId: number | string }>(),
);

export const loadBoondocks = createAction(
  BoondockActionTypes.LoadBoondocks,
  props<{ payload?: IBoondockWhere }>(),
);

export const clearBoondocks = createAction(
  BoondockActionTypes.ClearBoondocks,
);

export const loadBoondockFailed = createAction(
  BoondockActionTypes.LoadBoondockFailed,
  props<{ error: string }>(),
);

export const loadBoondockRatings = createAction(
  BoondockActionTypes.LoadBoondockRatings,
  props<{ boondockId?: number }>(),
);

export const uploadBoondockImage = createAction(
  BoondockActionTypes.UploadBoondockImage,
  props<{ formData: FormData; data: { boondockId?: number; syncId?: string } }>(),
);

export const uploadBoondockReviewImage = createAction(
  BoondockActionTypes.UploadBoondockReviewImage,
  props<{ formData: FormData; data: { boondockId: number } }>(),
);

export const boondockReviewImageUploaded = createAction(
  BoondockActionTypes.BoondockReviewImageUploaded,
  props<{ id: number; photos: IPhoto[] }>(),
);

export const boondockReviewImageUploadFailed = createAction(
  BoondockActionTypes.BoondockReviewImageUploadFailed,
  props<{ error: string }>(),
);

export const removeImageFromBoondock = createAction(
  BoondockActionTypes.RemoveImageFromBoondock,
  props<{ boondockId: number; index: number }>(),
);

export const removeBoondock = createAction(
  BoondockActionTypes.RemoveBoondock,
  props<{ boondockId: number | string }>(),
);

export const removeBoondockFailed = createAction(
  BoondockActionTypes.RemoveBoondockFailed,
  props<{ error: string }>(),
);

export const restoreBoondock = createAction(
  BoondockActionTypes.RestoreBoondock,
  props<{ boondockId: number }>(),
);

export const removeActivity = createAction(
  BoondockActionTypes.RemoveActivity,
  props<{ boondock: IBoondock; activity: Activity }>(),
);

export const removeAmenity = createAction(
  BoondockActionTypes.RemoveAmenity,
  props<{ boondock: IBoondock; amenity: Amenity }>(),
);

export const removePolicy = createAction(
  BoondockActionTypes.RemovePolicy,
  props<{ boondock: IBoondock; policy: Policy }>(),
);

export const removeSignalReception = createAction(
  BoondockActionTypes.RemoveSignalReception,
  props<{ boondock: IBoondock; signalReception: SignalReception }>(),
);

export const boondockCreated = createAction(BoondockActionTypes.BoondockCreated, props<{ boondock: IBoondock }>());

export const boondockImageRemoved = createAction(
  BoondockActionTypes.BoondockImageRemoved,
  props<{ boondock: IBoondock }>(),
);

export const boondockRemoved = createAction(
  BoondockActionTypes.BoondockRemoved,
  props<{ boondock: IBoondock }>(),
);

export const boondockRestored = createAction(BoondockActionTypes.BoondockRestored, props<{ boondock: IBoondock }>());

export const boondockLoaded = createAction(BoondockActionTypes.BoondockLoaded, props<{ boondock: IBoondock }>());

export const boondocksLoaded = createAction(BoondockActionTypes.BoondocksLoaded, props<{ boondocks: IBoondock[] }>());

export const boondockUpdated = createAction(BoondockActionTypes.BoondockUpdated, props<{ boondock: IBoondock }>());

export const updateBoondock = createAction(
  BoondockActionTypes.UpdateBoondock,
  props<{ boondock: IBoondock }>(),
);

export const updateBoondockOffline = createAction('[Boondock Offline] Update', props<{ payload: IBoondock }>());

export const processPendingBoondock = createAction('[Boondock Offline] Save Offline Boondock To Server', props<{ boondock: IBoondock }>());

export const processPendingBoondockSuccess = createAction(
  '[Boondock Offline] Process Pending Boondock Upload Success',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props<{ boondock: IBoondock }>(),
);

export const processPendingBoondockFailed = createAction(
  '[Boondock Offline] Process Pending Boondock Upload Failure',
  props<{ error: HttpErrorResponse; boondock?: IBoondock }>(),
);

export const processPendingBoondockPhoto = createAction(
  '[Boondock Offline] Process Pending Boondock Image Upload',
  props<{ boondockId: number; photo: IPhoto }>(),
);

export const processPendingBoondockPhotoSuccess = createAction(
  '[Boondock Offline] Process Pending Boondock Image Upload Success',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props<{ boondockId: number; photo: IPhoto }>(),
);

export const processPendingBoondockPhotoFailed = createAction(
  '[Boondock Offline] Process Pending Boondock Image Upload Failure',
  props<{ error: any; boondockId?: number; photo?: IPhoto }>(),
);

export const updateBoondockFailed = createAction(
  BoondockActionTypes.UpdateBoondockFailed,
  props<{ error: string }>(),
);

export const boondockImagesUpdated = createAction(
  BoondockActionTypes.BoondockImagesUpdated,
  props<{ id?: number; syncId?: string; photos: IPhoto[] }>(),
);

export const enableBoondock = createAction(
  BoondockActionTypes.EnableBoondock,
  props<{ boondock: IBoondock }>(),
);

export const boondockEnabled = createAction(
  BoondockActionTypes.BoondockEnabled,
  props<{ boondockId: number | string }>(),
);

export const boondockEnabledFailed = createAction(
  BoondockActionTypes.BoondockEnabledFailed,
  props<{ body: string; boondockId?: number }>(),
);

export const disableBoondock = createAction(
  BoondockActionTypes.DisableBoondock,
  props<{ boondock: IBoondock }>(),
);

export const boondockDisabled = createAction(
  BoondockActionTypes.BoondockDisabled,
  props<{ boondockId: number | string }>(),
);

export const boondockDisabledFailed = createAction(
  BoondockActionTypes.BoondockDisabledFailed,
  props<{ error: string }>(),
);

export const selectBoondock = createAction(
  BoondockActionTypes.SelectBoondock,
  props<{ boondockId?: number | string }>(),
);

export const selectBoondockByBoondock = createAction(
  BoondockActionTypes.SelectBoondockByBoondock,
  props<{ boondock?: IBoondock }>(),
);

export const boondockSelected = createAction(
  BoondockActionTypes.BoondockSelected,
  props<{ boondock?: IBoondock }>(),
);

export const favoriteBoondock = createAction(
  BoondockActionTypes.FavoriteListing,
  props<{ point: IBoondockPoint }>(),
);

export const unfavoriteBoondock = createAction(
  BoondockActionTypes.UnfavoriteListing,
  props<{ point: IBoondockPoint }>(),
);

export const listingFavorCompleted = createAction(
  BoondockActionTypes.ListingFavorCompleted,
  props<{ id: number; favored: boolean }>(),
);

export const listingFavorFailed = createAction(
  BoondockActionTypes.ListingFavorFailed,
  props<{ error: string }>(),
);

export const recropImage = createAction(
  BoondockActionTypes.BoondockRecropImage,
  props<{ boondockToSave: IBoondock; photo: ISubPhoto }>(),
);

export const recropImageFailure = createAction(
  BoondockActionTypes.BoondockRecropImageFailure,
  props<{ error: string }>(),
);
